import { useState } from "react";
import { TextInput, Textarea, Select, Stack, Fieldset, Text, SimpleGrid, Badge } from "@mantine/core";
import { UserSelect } from "../../components/UserSelect.js";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { type Massnahme, MassnahmenStatus, MassnahmenPriority } from "@iso27001/shared";
import { massnahmenApi, massnahmenLinks } from "../../api/massnahmen.js";
import { ereignisseApi } from "../../api/ereignisse.js";
import { ApiError } from "../../api/client.js";
import { LinkedItemsList } from "../../components/LinkedItemsList.js";
import { LinkPickerModal } from "../../components/LinkPickerModal.js";
import { EntityModal } from "../../components/EntityModal.js";
import { useRecordAccess } from "../../components/use-record-access.js";
import { isMassnahmeClosed } from "../../components/record-state.js";
import { massnahmeStatusColor } from "../../components/status-colors.js";
import { AttachmentsSection } from "../../components/AttachmentsSection.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { useApiErrorMessage } from "../../i18n/api-error.js";
import { useLocale } from "../../i18n/use-locale.js";
import { datePickerFormat } from "../../i18n/format-date.js";

interface Props {
  opened: boolean;
  onClose: () => void;
  initial: Massnahme | null;
}

/** Truncate a string to `max` chars, appending "…" if cut. */
function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max) + "…";
}

export function MassnahmeFormModal({ opened, onClose, initial }: Props) {
  const qc = useQueryClient();
  const isEdit = !!initial;
  const access = useRecordAccess(!!initial && isMassnahmeClosed(initial.status));
  const { readOnly } = access;
  const [ereignisPickerOpen, setEreignisPickerOpen] = useState(false);
  const { t } = useTranslation();
  const locale = useLocale();
  const { label } = useEnumLabel();
  const apiError = useApiErrorMessage();

  const statusOptions = MassnahmenStatus.options.map((value) => ({ value, label: label("massnahmeStatus", value) }));
  const priorityOptions = MassnahmenPriority.options.map((value) => ({ value, label: label("priority", value) }));

  const form = useForm({
    initialValues: {
      code: initial?.code ?? "",
      description: initial?.description ?? "",
      notes: initial?.notes ?? "",
      responsibleUserId: initial?.responsibleUserId ?? null as number | null,
      operationalUserId: initial?.operationalUserId ?? null as number | null,
      // dueDate stored as Date object for DateInput; null when not set
      dueDate: initial?.dueDate ? new Date(initial.dueDate * 1000) : null as Date | null,
      status: initial?.status ?? "Offen",
      statusNote: initial?.statusNote ?? "",
      priority: initial?.priority ?? "" as string,
      origin: initial?.origin ?? "",
      externalAuditFinding: initial?.externalAuditFinding ?? "",
      internalAuditFinding: initial?.internalAuditFinding ?? "",
    },
  });

  const linkedRisikenQuery = useQuery({
    queryKey: ["massnahmen", initial?.id, "risiken"],
    queryFn: () => massnahmenLinks.listRisiken(initial!.id),
    enabled: !!initial?.id,
  });
  const linkedRisiken = linkedRisikenQuery.data?.items ?? [];

  const linkedEreignisseQuery = useQuery({
    queryKey: ["massnahmen", initial?.id, "ereignisse"],
    queryFn: () => massnahmenLinks.listEreignisse(initial!.id),
    enabled: !!initial?.id,
  });
  const linkedEreignisse = linkedEreignisseQuery.data?.items ?? [];

  const mut = useMutation({
    mutationFn: async (vals: typeof form.values) => {
      const payload: Record<string, unknown> = {
        description: vals.description,
        notes: vals.notes || null,
        responsibleUserId: vals.responsibleUserId ?? null,
        operationalUserId: vals.operationalUserId ?? null,
        // convert Date back to unix seconds
        dueDate: vals.dueDate ? Math.floor(vals.dueDate.getTime() / 1000) : null,
        status: vals.status as Massnahme["status"],
        statusNote: vals.statusNote || null,
        priority: (vals.priority || null) as Massnahme["priority"],
        origin: vals.origin || null,
        externalAuditFinding: vals.externalAuditFinding || null,
        internalAuditFinding: vals.internalAuditFinding || null,
      };
      // Include code only when editing or when user explicitly provided one on create
      if (isEdit || vals.code.trim() !== "") {
        payload.code = vals.code;
      }
      return isEdit
        ? massnahmenApi.update(initial!.id, payload as Parameters<typeof massnahmenApi.update>[1])
        : massnahmenApi.create(payload as Parameters<typeof massnahmenApi.create>[0]);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["massnahmen"] });
      notifications.show({ message: isEdit ? t("massnahmen.form.notify.updated") : t("massnahmen.form.notify.created"), color: "teal" });
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "Conflict") {
        form.setFieldError("code", t("massnahmen.error.codeConflict"));
      } else {
        notifications.show({ message: apiError(e, { fallback: "massnahmen.error.saveFailed" }), color: "red" });
      }
    },
  });

  return (
    <EntityModal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={isEdit ? t("massnahmen.form.title") : t("massnahmen.newMeasure")}
      code={initial?.code}
      badge={initial && <Badge color={massnahmeStatusColor[initial.status]} variant="light">{label("massnahmeStatus", initial.status)}</Badge>}
      readOnly={readOnly}
      closed={access.closed}
      onUnlock={access.unlock}
      onSubmit={form.onSubmit((vals) => mut.mutate(vals))}
      saving={mut.isPending}
      submitLabel={isEdit ? t("common.action.save") : t("common.action.createSubmit")}
    >
      <Stack>
        {/* Read-only drops the code input (it is in the title), leaving three short values. */}
        <SimpleGrid cols={{ base: 1, sm: readOnly ? 3 : 2 }}>
          {!readOnly && (
            <TextInput
              label={isEdit ? t("common.field.code") : t("common.field.codeOptional")}
              description={isEdit ? undefined : t("massnahmen.form.code.autoHint")}
              // Hint under the input, so the field lines up with its neighbours in the row.
              inputWrapperOrder={["label", "input", "description", "error"]}
              required={isEdit}
              maxLength={80}
              placeholder={isEdit ? undefined : t("common.field.codeAutoPlaceholder")}
              {...form.getInputProps("code")}
            />
          )}
          <Select
            label={t("common.field.status")}
            data={statusOptions}
            readOnly={readOnly}
            {...form.getInputProps("status")}
          />
          <Select
            label={t("massnahmen.field.priority")}
            data={priorityOptions}
            readOnly={readOnly}
            clearable={!readOnly}
            {...form.getInputProps("priority")}
          />
          <DateInput
            label={t("massnahmen.field.dueDate")}
            placeholder={t("common.field.datePlaceholder")}
            readOnly={readOnly}
            clearable={!readOnly}
            valueFormat={datePickerFormat(locale)}
            value={form.values.dueDate}
            onChange={(val) => form.setFieldValue("dueDate", val)}
            error={form.errors.dueDate}
          />
        </SimpleGrid>
        <Textarea label={t("common.field.description")} required readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={5000} {...form.getInputProps("description")} />
        <Textarea label={t("massnahmen.form.notes.label")} readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={5000} {...form.getInputProps("notes")} />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <UserSelect
            label={t("massnahmen.field.responsible")}
            placeholder={t("common.field.userPlaceholder")}
            readOnly={readOnly}
            clearable={!readOnly}
            value={form.values.responsibleUserId}
            onChange={(val) => form.setFieldValue("responsibleUserId", val)}
            error={form.errors.responsibleUserId}
          />
          <UserSelect
            label={t("massnahmen.form.operationalUser.label")}
            placeholder={t("common.field.userPlaceholder")}
            readOnly={readOnly}
            clearable={!readOnly}
            value={form.values.operationalUserId}
            onChange={(val) => form.setFieldValue("operationalUserId", val)}
            error={form.errors.operationalUserId}
          />
        </SimpleGrid>
        <Textarea label={t("massnahmen.form.statusNote.label")} readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={2000} {...form.getInputProps("statusNote")} />
        <TextInput label={t("massnahmen.form.origin.label")} readOnly={readOnly} maxLength={2000} {...form.getInputProps("origin")} />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Textarea label={t("massnahmen.form.externalAuditFinding.label")} readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={2000} {...form.getInputProps("externalAuditFinding")} />
          <Textarea label={t("massnahmen.form.internalAuditFinding.label")} readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={2000} {...form.getInputProps("internalAuditFinding")} />
        </SimpleGrid>
        {/* Verknüpfte Risiken — read-only backlinks (links are managed from the Risiko side) */}
        <Fieldset legend={t("massnahmen.form.linkedRisiken.title")}>
          {initial?.id ? (
            <LinkedItemsList
              items={linkedRisiken.map((r) => ({
                id: r.id,
                label: `${r.code}${r.threatDescription ? ` — ${truncate(r.threatDescription, 60)}` : ""}`,
              }))}
              onUnlink={async () => { /* read-only: no-op */ }}
              onAddClick={() => { /* read-only: managed from Risiko side */ }}
              emptyText={t("massnahmen.form.linkedRisiken.emptyText")}
              canEdit={false}
            />
          ) : (
            <Text size="sm" c="dimmed">{t("massnahmen.form.linkedRisiken.saveHint")}</Text>
          )}
        </Fieldset>

        {/* Verknüpfte Ereignisse — bidirectional, managed from this side */}
        <Fieldset legend={t("massnahmen.form.linkedEreignisse.title")}>
          {initial?.id ? (
            <>
              <LinkedItemsList
                items={linkedEreignisse.map((e) => ({
                  id: e.id,
                  label: `${e.code} — ${truncate(e.description, 60)}`,
                }))}
                onUnlink={async (id) => {
                  await massnahmenLinks.unlinkEreignis(initial.id, id);
                  qc.invalidateQueries({ queryKey: ["massnahmen", initial.id, "ereignisse"] });
                }}
                onAddClick={() => setEreignisPickerOpen(true)}
                emptyText={t("massnahmen.form.linkedEreignisse.emptyText")}
                canEdit={!readOnly}
              />
              <LinkPickerModal
                opened={ereignisPickerOpen}
                onClose={() => setEreignisPickerOpen(false)}
                title={t("massnahmen.form.linkedEreignisse.linkModalTitle")}
                loadCandidates={(q) => ereignisseApi.list({ q, pageSize: 200 }).then((r) => r.items)}
                excludeIds={linkedEreignisse.map((e) => e.id)}
                labelOf={(e) => `${e.code} — ${truncate(e.description, 80)}`}
                idOf={(e) => e.id}
                onPick={async (id) => {
                  await massnahmenLinks.linkEreignis(initial.id, id);
                  qc.invalidateQueries({ queryKey: ["massnahmen", initial.id, "ereignisse"] });
                }}
              />
            </>
          ) : (
            <Text size="sm" c="dimmed">{t("massnahmen.form.linkedEreignisse.saveHint")}</Text>
          )}
        </Fieldset>

        {initial?.id && (
          <Fieldset legend={t("attachments.sectionTitle")}>
            <AttachmentsSection entity="massnahme" entityId={initial.id} canEdit={!readOnly} />
          </Fieldset>
        )}
      </Stack>
    </EntityModal>
  );
}
